import React, { useState, useEffect } from 'react';
import esgSupplierPerformanceService from '../../../services/esg/esgSupplierPerformanceService';

const ESGSupplierPerformanceMonitoring = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const unsubscribe = esgSupplierPerformanceService.subscribeToPerformanceData((data) => {
      setPerformanceData(data);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Filter suppliers based on current filters
  const filteredSuppliers = performanceData?.supplierPerformance?.filter(supplier => {
    const matchesSearch = !searchTerm || 
      supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = filterRegion === 'all' || supplier.region === filterRegion;
    const matchesCategory = filterCategory === 'all' || supplier.category === filterCategory;
    const matchesTier = filterTier === 'all' || supplier.tier === filterTier;
    
    return matchesSearch && matchesRegion && matchesCategory && matchesTier;
  }) || [];

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeColor = (score) => {
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 80) return 'bg-blue-100 text-blue-800';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'Improving': return '📈';
      case 'Declining': return '📉';
      default: return '➡️';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAlertSeverityColor = (severity) => {
    switch (severity) {
      case 'Low': return 'bg-blue-100 text-blue-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderDashboardTab = () => (
    <div className="space-y-6">
      {/* KPI Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Suppliers</p>
              <p className="text-2xl font-bold text-gray-900">{performanceData?.performanceAnalytics?.overview?.totalSuppliers}</p>
            </div>
            <div className="text-3xl">👥</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Active performance monitoring
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average ESG Score</p>
              <p className={`text-2xl font-bold ${getScoreColor(performanceData?.performanceAnalytics?.overview?.averageScore)}`}>
                {performanceData?.performanceAnalytics?.overview?.averageScore}
              </p>
            </div>
            <div className="text-3xl">📊</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Out of 100 points
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Top Performers</p>
              <p className="text-2xl font-bold text-green-600">{performanceData?.performanceAnalytics?.overview?.topPerformers}</p>
            </div>
            <div className="text-3xl">🏆</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Score ≥ 90 points
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">At Risk</p>
              <p className="text-2xl font-bold text-red-600">{performanceData?.performanceAnalytics?.overview?.atRisk}</p>
            </div>
            <div className="text-3xl">⚠️</div>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            High/Critical risk level
          </div>
        </div>
      </div>

      {/* Performance by Category */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance by Category</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {performanceData?.performanceAnalytics?.byCategory?.map((category, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 text-sm">{category.category}</h4>
                <span className="text-lg">{getTrendIcon(category.trend)}</span>
              </div>
              <div className="text-2xl font-bold text-purple-600 mb-1">{category.averageScore}</div>
              <div className="text-xs text-gray-500">{category.suppliers} suppliers</div>
            </div>
          ))}
        </div>
      </div>

      {/* Real-time KPI Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Environmental KPIs */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Environmental KPIs</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Carbon Emissions</span>
              <div className="text-right">
                <div className="font-medium">{performanceData?.kpiDashboard?.environmental?.carbonEmissions?.current.toLocaleString()} tCO2e</div>
                <div className="text-xs text-green-600">-{performanceData?.kpiDashboard?.environmental?.carbonEmissions?.reduction}% vs target</div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Waste Reduction</span>
              <div className="text-right">
                <div className="font-medium">{performanceData?.kpiDashboard?.environmental?.wasteReduction?.current}%</div>
                <div className="text-xs text-green-600">+{performanceData?.kpiDashboard?.environmental?.wasteReduction?.improvement}% improvement</div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Renewable Energy</span>
              <div className="text-right">
                <div className="font-medium">{performanceData?.kpiDashboard?.environmental?.renewableEnergy?.current}%</div>
                <div className="text-xs text-blue-600">+{performanceData?.kpiDashboard?.environmental?.renewableEnergy?.increase}% increase</div>
              </div>
            </div>
          </div>
        </div>

        {/* Social KPIs */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Social KPIs</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Labor Compliance</span>
              <div className="text-right">
                <div className="font-medium">{performanceData?.kpiDashboard?.social?.laborCompliance?.current}%</div>
                <div className="text-xs text-gray-500">{performanceData?.kpiDashboard?.social?.laborCompliance?.violations} violations</div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Diversity & Inclusion</span>
              <div className="text-right">
                <div className="font-medium">{performanceData?.kpiDashboard?.social?.diversityInclusion?.current}%</div>
                <div className="text-xs text-green-600">+{performanceData?.kpiDashboard?.social?.diversityInclusion?.improvement}% improvement</div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Community Investment</span>
              <div className="text-right">
                <div className="font-medium">${performanceData?.kpiDashboard?.social?.communityImpact?.investmentMillion}M</div>
                <div className="text-xs text-gray-500">{performanceData?.kpiDashboard?.social?.communityImpact?.beneficiaries.toLocaleString()} beneficiaries</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regional Performance */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance by Region</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suppliers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Top Performer</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {performanceData?.performanceAnalytics?.byRegion?.map((region, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{region.region}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{region.suppliers}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getScoreColor(region.averageScore)}`}>
                      {region.averageScore}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{region.topPerformer}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSuppliersTab = () => (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search suppliers..."
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
              <select
                value={filterRegion}
                onChange={(e) => setFilterRegion(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Regions</option>
                <option value="North America">North America</option>
                <option value="Europe">Europe</option>
                <option value="Asia Pacific">Asia Pacific</option>
                <option value="Latin America">Latin America</option>
                <option value="Middle East">Middle East</option>
                <option value="Africa">Africa</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Categories</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Technology">Technology</option>
                <option value="Services">Services</option>
                <option value="Logistics">Logistics</option>
                <option value="Energy">Energy</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
              <select
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Tiers</option>
                <option value="Tier 1">Tier 1</option>
                <option value="Tier 2">Tier 2</option>
                <option value="Tier 3">Tier 3</option>
              </select>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Showing {filteredSuppliers.length} of {performanceData?.supplierPerformance?.length || 0} suppliers
          </div>
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSuppliers.map(supplier => (
          <div key={supplier.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedSupplier(supplier)}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900 truncate">{supplier.name}</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreBadgeColor(supplier.overallScore)}`}>
                  {supplier.overallScore}
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-medium">{supplier.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Region:</span>
                  <span>{supplier.region}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tier:</span>
                  <span>{supplier.tier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Contract Value:</span>
                  <span className="font-medium">{supplier.contractValue}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Risk Level:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(supplier.riskLevel)}`}>
                    {supplier.riskLevel}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Trend:</span>
                  <span className="flex items-center">
                    {getTrendIcon(supplier.performanceTrend)} {supplier.performanceTrend}
                  </span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="text-gray-600">Environmental</div>
                    <div className={`font-medium ${getScoreColor(supplier.environmentalScore)}`}>{supplier.environmentalScore}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Social</div>
                    <div className={`font-medium ${getScoreColor(supplier.socialScore)}`}>{supplier.socialScore}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Governance</div>
                    <div className={`font-medium ${getScoreColor(supplier.governanceScore)}`}>{supplier.governanceScore}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Quality</div>
                    <div className={`font-medium ${getScoreColor(supplier.qualityScore)}`}>{supplier.qualityScore}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderComplianceTab = () => (
    <div className="space-y-6">
      {/* Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{performanceData?.performanceAnalytics?.overview?.compliant}</div>
            <div className="text-sm text-gray-600">Compliant Suppliers</div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{performanceData?.kpiDashboard?.governance?.ethicsCompliance?.current}%</div>
            <div className="text-sm text-gray-600">Ethics Compliance</div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{performanceData?.kpiDashboard?.governance?.dataPrivacy?.certifications}</div>
            <div className="text-sm text-gray-600">Active Certifications</div>
          </div>
        </div>
      </div>

      {/* Compliance Framework Status */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ESG Framework Compliance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {performanceData?.complianceMonitoring?.frameworks?.map((framework, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{framework.framework}</h4>
                <span className={`text-xs px-2 py-1 rounded ${
                  framework.status === 'Compliant' ? 'bg-green-100 text-green-800' :
                  framework.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {framework.status}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Score:</span>
                  <span className="font-medium">{framework.score}/100</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Audit:</span>
                  <span>{framework.lastAudit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Next Audit:</span>
                  <span>{framework.nextAudit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Critical Issues:</span>
                  <span className={framework.criticalIssues > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                    {framework.criticalIssues}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Auditor:</span>
                  <span className="text-xs">{framework.auditor}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Areas */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Areas Monitoring</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Area</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incidents</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mitigation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Review</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {performanceData?.complianceMonitoring?.riskAreas?.map((risk, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{risk.area}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskColor(risk.riskLevel)}`}>
                      {risk.riskLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{risk.incidents}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{risk.mitigation}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{risk.owner}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{risk.lastReview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAlertsTab = () => (
    <div className="space-y-6">
      {/* Alert Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {['Open', 'In Progress', 'Resolved', 'Escalated'].map(status => {
          const count = performanceData?.performanceAlerts?.filter(alert => alert.status === status).length || 0;
          return (
            <div key={status} className="bg-white p-6 rounded-lg shadow">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{count}</div>
                <div className="text-sm text-gray-600">{status} Alerts</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Alerts */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Performance Alerts</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alert</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {performanceData?.performanceAlerts?.slice(0, 15).map(alert => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{alert.message}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{alert.supplier}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{alert.type}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAlertSeverityColor(alert.severity)}`}>
                      {alert.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      alert.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                      alert.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                      alert.status === 'Escalated' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {alert.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{alert.assignedTo}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(alert.timestamp).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => esgSupplierPerformanceService.updateAlertStatus(alert.id, 'In Progress')}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      Update
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderBenchmarkingTab = () => (
    <div className="space-y-6">
      {/* Industry Benchmarks */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry Benchmarking</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Our Average</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry Average</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Top Quartile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gap</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ranking</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {performanceData?.benchmarkingData?.industryBenchmarks?.map((benchmark, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{benchmark.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${getScoreColor(benchmark.ourAverage)}`}>
                      {benchmark.ourAverage}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{benchmark.industryAverage}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{benchmark.topQuartile}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${benchmark.gap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {benchmark.gap >= 0 ? '+' : ''}{benchmark.gap}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{benchmark.ranking} percentile</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Competitor Analysis */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Competitor Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {performanceData?.benchmarkingData?.competitorAnalysis?.map((competitor, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{competitor.name}</h4>
                <span className={`text-lg font-bold ${getScoreColor(competitor.overallScore)}`}>
                  {competitor.overallScore}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <div className="font-medium text-gray-700 mb-1">Strengths:</div>
                  <div className="flex flex-wrap gap-1">
                    {competitor.strengths.map((strength, i) => (
                      <span key={i} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        {strength}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-700 mb-1">Gaps:</div>
                  <div className="flex flex-wrap gap-1">
                    {competitor.gaps.map((gap, i) => (
                      <span key={i} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                        {gap}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Market Share: </span>
                  <span className="font-medium">{competitor.marketShare}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Improvement Recommendations */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Improvement Recommendations</h3>
        <div className="space-y-4">
          {performanceData?.improvementRecommendations?.map((rec, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      rec.priority === 'High' ? 'bg-red-100 text-red-800' :
                      rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {rec.priority} Priority
                    </span>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      {rec.category}
                    </span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{rec.recommendation}</h4>
                  <p className="text-sm text-gray-600 mb-3">{rec.impact}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="font-medium text-gray-700">Timeline:</div>
                  <div className="text-gray-600">{rec.timeline}</div>
                </div>
                <div>
                  <div className="font-medium text-gray-700">Investment:</div>
                  <div className="text-gray-600">{rec.investment}</div>
                </div>
                <div>
                  <div className="font-medium text-gray-700">ROI:</div>
                  <div className="text-gray-600">{rec.roi}</div>
                </div>
                <div>
                  <button className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'dashboard', label: 'Performance Dashboard', icon: '📊' },
    { id: 'suppliers', label: 'Supplier Portfolio', icon: '🏢' },
    { id: 'compliance', label: 'Compliance Monitoring', icon: '✅' },
    { id: 'alerts', label: 'Performance Alerts', icon: '🚨' },
    { id: 'benchmarking', label: 'Benchmarking & Analytics', icon: '📈' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">ESG Supplier Performance Monitoring</h1>
            <p className="text-purple-100 mt-2">
              Real-time performance tracking, KPI monitoring, and ESG compliance analytics for comprehensive supplier management
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{performanceData?.performanceAnalytics?.overview?.averageScore || 0}</div>
            <div className="text-purple-100">Avg ESG Score</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && renderDashboardTab()}
          {activeTab === 'suppliers' && renderSuppliersTab()}
          {activeTab === 'compliance' && renderComplianceTab()}
          {activeTab === 'alerts' && renderAlertsTab()}
          {activeTab === 'benchmarking' && renderBenchmarkingTab()}
        </div>
      </div>

      {/* Supplier Detail Modal */}
      {selectedSupplier && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Performance Details: {selectedSupplier.name}</h3>
              <button
                onClick={() => setSelectedSupplier(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Performance Scores</h4>
                  <div className="mt-2 space-y-2">
                    <div className="flex justify-between">
                      <span>Overall Score:</span>
                      <span className={`font-bold ${getScoreColor(selectedSupplier.overallScore)}`}>
                        {selectedSupplier.overallScore}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Environmental:</span>
                      <span className={`font-medium ${getScoreColor(selectedSupplier.environmentalScore)}`}>
                        {selectedSupplier.environmentalScore}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Social:</span>
                      <span className={`font-medium ${getScoreColor(selectedSupplier.socialScore)}`}>
                        {selectedSupplier.socialScore}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Governance:</span>
                      <span className={`font-medium ${getScoreColor(selectedSupplier.governanceScore)}`}>
                        {selectedSupplier.governanceScore}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quality:</span>
                      <span className={`font-medium ${getScoreColor(selectedSupplier.qualityScore)}`}>
                        {selectedSupplier.qualityScore}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery:</span>
                      <span className={`font-medium ${getScoreColor(selectedSupplier.deliveryScore)}`}>
                        {selectedSupplier.deliveryScore}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Key Metrics</h4>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-600">Quality Defects:</span> {selectedSupplier.kpis.qualityDefects}</div>
                    <div><span className="text-gray-600">On-Time Delivery:</span> {selectedSupplier.kpis.onTimeDelivery}</div>
                    <div><span className="text-gray-600">Cost Savings:</span> {selectedSupplier.kpis.costSavings}</div>
                    <div><span className="text-gray-600">Carbon Reduction:</span> {selectedSupplier.kpis.carbonReduction}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Supplier Information</h4>
                  <div className="mt-2 space-y-1 text-sm">
                    <div><span className="font-medium">Category:</span> {selectedSupplier.category}</div>
                    <div><span className="font-medium">Region:</span> {selectedSupplier.region}</div>
                    <div><span className="font-medium">Tier:</span> {selectedSupplier.tier}</div>
                    <div><span className="font-medium">Contract Value:</span> {selectedSupplier.contractValue}</div>
                    <div><span className="font-medium">Relationship:</span> {selectedSupplier.relationship}</div>
                    <div><span className="font-medium">Status:</span> {selectedSupplier.status}</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Certifications</h4>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedSupplier.certifications.map(cert => (
                      <span key={cert} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Recent Issues</h4>
                  <div className="mt-2 space-y-1 text-sm">
                    {selectedSupplier.issues.length > 0 ? (
                      selectedSupplier.issues.map((issue, i) => (
                        <div key={i} className="flex justify-between">
                          <span>{issue.type}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            issue.status === 'Resolved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {issue.status}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500">No recent issues</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setSelectedSupplier(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700">
                View Full Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ESGSupplierPerformanceMonitoring;